import { Link } from "@/lib/nav";

import Banner from "@buildpy/ui/banner";
import SectionCard from "@buildpy/ui/section-card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@buildpy/ui/table";
import MetaSep from "@/components/meta-sep";
import Money from "@/components/money";
import { formatDateTime, formatQty } from "@buildpy/core/lib/format";
import { contractVatModeOf, currentTotalOf } from "@buildpy/core/modules/change-order/calc";
import { statementPath } from "@buildpy/core/modules/change-order/current-statement";
import { CHANGE_ORDER_SENT_STALE_NOTICE } from "@buildpy/core/modules/change-order/messages";
import { changeOrderStartBlock } from "@buildpy/core/modules/change-order/start-block";
import {
  acceptBlockReason,
  extendAllowedFor,
  isClosed,
  sendBlockReason,
  warrantyExtendBlock,
  type ChangeOrderGate,
} from "@buildpy/core/modules/change-order/transitions";
import { CHANGE_ORDER_DIRECTION_LABEL } from "@buildpy/core/modules/change-order/types";
import ChangeOrderActions from "@/modules/change-order/ui/change-order-actions";
import ChangeOrderBlock from "@/modules/change-order/ui/change-order-block";
import ChangeOrderEditor from "@/modules/change-order/ui/change-order-editor";
import ChangeOrderPreview from "@/modules/change-order/ui/change-order-preview";
import ChangeOrderRecreateButton from "@/modules/change-order/ui/change-order-recreate-button";
import ChangeOrderStatusBadge from "@/modules/change-order/ui/status-badge";
import { sourceTagOf } from "@buildpy/core/modules/change-order/view";
import type { ChangeOrderView } from "@buildpy/core/views/g2b/change-order";
import PageHeader from "@/shell/page-header";
import { useSignedUrls } from "@/lib/signed-url";

// SectionCard 안 촘촘한 표 — 공통 Table 기본(h-12·p-4)을 줄이고 왼쪽 여백을 없앤다.
const HEAD = "h-auto py-1 pl-0 pr-2 text-xs font-normal";
const CELL = "py-1.5 pl-0 pr-2";

// 항목 아래 공종·공간과 원 견적 줄(없으면 「추가 항목」) 한 줄.
const SourceTag = ({ line }: { line: Parameters<typeof sourceTagOf>[0] }) => {
  const tag = sourceTagOf(line);
  if (!tag.place && !tag.origin) return null;
  return (
    <span className="block text-xs text-muted-foreground" data-testid="change-order-item-source">
      {[tag.place, tag.origin].filter(Boolean).join(" · ")}
    </span>
  );
};

const DECIDER_LABEL = { CUSTOMER: "고객 링크", STAFF: "담당자 기록" } as const;

// 고객 손서명 — 화면 주소를 서명 URL 로 바꿔 그린다(받기 전엔 비워 둔다).
const SignatureImage = ({ path }: { path: string }) => {
  const src = useSignedUrls([path])(path);
  if (!src) return null;
  return <img alt="고객 서명" className="ml-auto h-16 w-auto rounded-sm border border-border" data-testid="change-order-signature" src={src} />;
};

// 변경공사 상세 — 작성 중이면 편집·미리보기, 보낸 뒤로는 굳은 문서를 읽기 전용으로.
const Page = ({ data }: { data: ChangeOrderView }) => {
  const { contractDetail, detail, project, siblings, liveLink, shareUrl, preview, sourceTrades, stale } = data;

  const { contract, payments } = contractDetail;
  const co = detail.change_order;
  const gate: ChangeOrderGate = {
    status: co.status,
    contract_status: contract.status,
    project_status: project?.project.status ?? "CLOSED",
    other_sent: siblings.some((s) => s.status === "SENT" && s.id !== co.id),
    item_count: detail.items.length,
    extend_days: co.extend_days,
  };
  const ended = contract.status === "TERMINATED" || contract.status === "COMPLETED";
  const current = currentTotalOf(contract, siblings).current;
  // 작성 중인데 계약·현장이 이미 끝났으면 편집기 대신 이유와 갈 곳을 보여 준다.
  const blocked = co.status === "DRAFT" ? changeOrderStartBlock(contract, project?.project ?? null, "draft") : null;
  // 유지보수로 넘어간 현장에 공기 증감이 남아 있으면 보내기·승인이 막힌 이유를 미리보기 위에 드러낸다.
  const warrantyBlock = isClosed(co.status) || ended || blocked ? null : warrantyExtendBlock(gate);

  return (
    <>
      <PageHeader
        actions={
          isClosed(co.status) || ended ? undefined : (
            <ChangeOrderActions
              acceptBlock={stale ? CHANGE_ORDER_SENT_STALE_NOTICE : acceptBlockReason(gate)}
              changeOrderId={co.id}
              contractId={contract.id}
              sendBlock={sendBlockReason({ ...gate, live_link: liveLink })}
              shareUrl={shareUrl}
              status={co.status}
            />
          )
        }
        meta={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-3 md:gap-x-2 md:gap-y-1">
            <ChangeOrderStatusBadge status={co.status} />
            <Link className="tap-target relative underline-offset-2 hover:underline" href={`/app/contracts/${contract.id}`}>
              계약 {contract.contract_no} · {contract.title}
            </Link>
            <MetaSep />
            <Link className="tap-target relative underline-offset-2 hover:underline" href={statementPath(contract.id)}>
              현재 내역서
            </Link>
            {project && (
              <>
                <MetaSep />
                <Link className="tap-target relative underline-offset-2 hover:underline" href={`/app/projects/${project.project.id}`}>
                  현장 보기
                </Link>
              </>
            )}
          </span>
        }
        title={`변경공사 ${co.seq}차 · ${co.title}`}
      />

      {blocked && <ChangeOrderBlock block={blocked} />}

      {ended && !isClosed(co.status) && !blocked && (
        <SectionCard as="div" className="text-sm" padding="sm" data-testid="change-order-ended">
          계약이 종료돼 처리할 수 없습니다.
        </SectionCard>
      )}

      {stale && (
        <SectionCard
          as="div"
          className="flex flex-wrap items-center justify-between gap-2 border-destructive/40 text-sm"
          data-testid="change-order-sent-stale"
          padding="sm">
          <p className="text-destructive">{CHANGE_ORDER_SENT_STALE_NOTICE}</p>
          <ChangeOrderRecreateButton changeOrderId={co.id} contractId={contract.id} />
        </SectionCard>
      )}

      {co.status === "SENT" && !stale && (
        <SectionCard as="div" className="text-sm text-muted-foreground" padding="sm">
          고객이 계약서 링크에서 승인을 기다리고 있습니다. {shareUrl ? "" : "살아 있는 고객 링크가 없습니다 — 종이·구두 합의는 「승인 기록」으로 남겨 주세요."}
        </SectionCard>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
        <div className="flex min-w-0 flex-col gap-y-4">
          {co.status === "DRAFT" && !ended && !blocked ? (
            <ChangeOrderEditor
              key={co.rev}
              currentTotal={current}
              extendAllowed={extendAllowedFor(project?.project.status)}
              initial={{
                title: co.title,
                reason: co.reason,
                source_log_id: co.source_log_id,
                extend_days: co.extend_days,
                items: detail.items.map((it) => ({
                  direction: it.direction,
                  item: it.item,
                  unit: it.unit,
                  qty: it.qty,
                  material_unit_price: it.material_unit_price,
                  labor_unit_price: it.labor_unit_price,
                  note: it.note,
                  source_quote_item_id: it.source_quote_item_id ?? null,
                })),
              }}
              mode={{ kind: "edit", contractId: contract.id, changeOrderId: co.id, rev: co.rev }}
              payments={payments.map(({ id, seq, ratio_bp, amount, paid_at }) => ({ id, seq, ratio_bp, amount, paid_at }))}
              sourceTrades={sourceTrades}
              vatMode={contractVatModeOf(contract)}
            />
          ) : (
            <SectionCard className="text-sm" padding="sm" data-testid="change-order-items">
              <h2 className="mb-2 text-sm font-semibold">변경 항목</h2>
              {co.reason && <p className="mb-2 whitespace-pre-wrap text-muted-foreground">{co.reason}</p>}
              {detail.items.length === 0 ? (
                <p className="text-muted-foreground">금액 변경 항목이 없습니다.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className={HEAD}>구분</TableHead>
                      <TableHead className={HEAD}>항목</TableHead>
                      <TableHead className={`${HEAD} text-right`}>수량</TableHead>
                      <TableHead className={`${HEAD} pr-0 text-right`}>금액</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detail.items.map((it) => (
                      <TableRow key={it.id}>
                        <TableCell className={`${CELL} whitespace-nowrap`}>{CHANGE_ORDER_DIRECTION_LABEL[it.direction]}</TableCell>
                        <TableCell className={CELL}>
                          {it.item}
                          {it.note && <span className="ml-1 text-xs text-muted-foreground">{it.note}</span>}
                          <SourceTag line={it} />
                        </TableCell>
                        <TableCell className={`${CELL} text-right tabular-nums whitespace-nowrap`}>
                          {formatQty(it.qty)} {it.unit}
                        </TableCell>
                        <TableCell className={`${CELL} pr-0 text-right tabular-nums whitespace-nowrap`}>
                          <Money value={it.direction === "DEDUCT" ? -it.amount : it.amount} signed />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </SectionCard>
          )}
        </div>

        <div className="flex flex-col gap-y-4">
          {warrantyBlock && (
            <div data-testid="change-order-warranty-extend">
              <Banner tone="warning">{warrantyBlock}</Banner>
            </div>
          )}
          {preview.data ? (
            <ChangeOrderPreview data={preview.data} draft={preview.draft} />
          ) : preview.error ? (
            <SectionCard as="p" className="border-destructive/40 text-sm text-destructive" data-testid="change-order-preview-error" padding="sm">
              {preview.error}
            </SectionCard>
          ) : null}

          <SectionCard className="text-sm" padding="sm">
            <h2 className="mb-2 text-sm font-semibold">기록</h2>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
              <dt className="text-muted-foreground">만든 때</dt>
              <dd className="text-right tabular-nums">{formatDateTime(co.created_at)}</dd>
              <dt className="text-muted-foreground">보낸 때</dt>
              <dd className="text-right tabular-nums">{formatDateTime(co.sent_at)}</dd>
              <dt className="text-muted-foreground">결정</dt>
              <dd className="text-right tabular-nums">
                {formatDateTime(co.decided_at)}
                {co.decided_by && ` · ${DECIDER_LABEL[co.decided_by]}`}
              </dd>
              {co.decided_name && (
                <>
                  <dt className="text-muted-foreground">승인자</dt>
                  <dd className="text-right">{co.decided_name}</dd>
                </>
              )}
              {data.signaturePath && (
                <>
                  <dt className="text-muted-foreground">서명</dt>
                  <dd>
                    <SignatureImage path={data.signaturePath} />
                  </dd>
                </>
              )}
              {co.decided_note && (
                <>
                  <dt className="text-muted-foreground">메모</dt>
                  <dd className="text-right whitespace-pre-wrap">{co.decided_note}</dd>
                </>
              )}
              {co.status === "REJECTED" && (
                <>
                  <dt className="text-muted-foreground">거절 사유</dt>
                  <dd className="text-right whitespace-pre-wrap">{co.reject_reason || "—"}</dd>
                </>
              )}
              {co.status === "VOID" && (
                <>
                  <dt className="text-muted-foreground">파기</dt>
                  <dd className="text-right tabular-nums">{formatDateTime(co.voided_at)}</dd>
                  <dt className="text-muted-foreground">파기 사유</dt>
                  <dd className="text-right whitespace-pre-wrap">{co.void_reason || "—"}</dd>
                </>
              )}
              {co.status === "ACCEPTED" && co.after_total !== null && (
                <>
                  <dt className="text-muted-foreground">변경 후 계약금액</dt>
                  <dd className="text-right font-semibold tabular-nums"><Money value={co.after_total} /></dd>
                </>
              )}
            </dl>
          </SectionCard>
        </div>
      </div>
    </>
  );
};

export default Page;
